import React, { useRef, useState } from 'react';
import { Box, Button, Popover, TextField, Typography } from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import { field, ghostBtn } from './uiKit';

const fmt = (d) => (d ? d.split('-').reverse().join('-') : '…');
// A complete yyyy-MM-dd with a sensible year; typing a year passes through 0002, 0020, … first.
const complete = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && Number(v.slice(0, 4)) >= 1900;

// A single button showing the chosen range; it opens a dropdown with From and To. Picking a From
// date moves straight on to the To picker, and picking the To date closes the dropdown.
export default function DateRangeDropdown({ from, to, onChange, placeholder = 'Select dates', title = 'Date range', height = 42 }) {
    const [anchor, setAnchor] = useState(null);
    const toRef = useRef(null);
    const active = Boolean(from || to);
    const inputSx = { ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } };

    const pickFrom = (value) => {
        // A To date that now sits before From would be an empty range, so it is cleared.
        onChange(value, to && value && to < value ? '' : to);
        if (!complete(value)) return;
        setTimeout(() => {
            const el = toRef.current;
            if (!el) return;
            el.focus();
            try { el.showPicker?.(); } catch { /* showPicker needs a recent user gesture; focus alone is enough */ }
        }, 0);
    };

    const pickTo = (value) => {
        onChange(from, value);
        if (complete(value)) setAnchor(null);
    };

    return (
        <>
            <Box
                role="button" tabIndex={0} aria-label={title}
                onClick={(e) => setAnchor(e.currentTarget)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAnchor(e.currentTarget); } }}
                sx={{
                    height, px: 1.6, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap',
                    bgcolor: active ? PRIMARY_LIGHT : '#fff', color: active ? PRIMARY : '#334155',
                    border: `1px solid ${active ? '#C9BEFB' : '#E5E7EB'}`, fontSize: 13, fontWeight: 700,
                    '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: '#C9BEFB', color: PRIMARY },
                }}
            >
                <CalendarMonthRoundedIcon sx={{ fontSize: 19, color: PRIMARY }} />
                {active ? `${fmt(from)} – ${fmt(to)}` : placeholder}
            </Box>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 2, mt: 1, width: 300, borderRadius: '12px' } } }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 1.6 }}>{title}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                    <TextField label="From" type="date" size="small" fullWidth value={from} onChange={(e) => pickFrom(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }} sx={inputSx} />
                    <TextField label="To" type="date" size="small" fullWidth value={to} onChange={(e) => pickTo(e.target.value)} inputRef={toRef}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }} sx={inputSx} />
                    {active && <Button onClick={() => onChange('', '')} sx={{ ...ghostBtn, height: 36 }}>Clear</Button>}
                </Box>
            </Popover>
        </>
    );
}
